import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getServices, createService, updateService, deleteService } from '../services/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, GripVertical } from 'lucide-react';

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title_en: '', title_zh: '',
    description_en: '', description_zh: '',
    icon: 'Home', image_url: '', order: 0, active: true
  });

  const fetchServices = async () => {
    try {
      const res = await getServices();
      setServices(res.data);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData(service);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingService(null);
    setFormData({
      title_en: '', title_zh: '',
      description_en: '', description_zh: '',
      icon: 'Home', image_url: '', order: services.length + 1, active: true
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editingService) {
        await updateService(editingService.id, formData);
        toast.success('Service updated');
      } else {
        await createService(formData);
        toast.success('Service created');
      }
      fetchServices();
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await deleteService(id);
      setServices(services.filter(s => s.id !== id));
      toast.success('Service deleted');
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const handleToggleActive = async (service) => {
    try {
      await updateService(service.id, { active: !service.active });
      setServices(services.map(s => s.id === service.id ? { ...s, active: !s.active } : s));
    } catch (error) {
      toast.error('Failed to update service');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Services</h1>
          <p className="text-slate-600">Manage your roofing services (English & Chinese)</p>
        </div>
        <Button onClick={handleCreate} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
          <Plus className="w-4 h-4 mr-2" /> Add Service
        </Button>
      </div>

      <div className="space-y-4">
        {services.map((service) => (
          <Card key={service.id} className={`p-4 bg-white border-2 border-slate-300 ${!service.active ? 'opacity-50' : ''}`}>
            <div className="flex items-center gap-4">
              <GripVertical className="w-5 h-5 text-slate-400 cursor-grab" />
              {service.image_url && (
                <img src={service.image_url} alt="" className="w-16 h-16 object-cover rounded-lg" />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-slate-900">{service.title_en}</h3>
                  <span className="text-slate-400">|</span>
                  <span className="text-slate-600">{service.title_zh}</span>
                </div>
                <p className="text-sm text-slate-500 line-clamp-1">{service.description_en}</p>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={service.active} onCheckedChange={() => handleToggleActive(service)} />
                <Button variant="outline" size="icon" onClick={() => handleEdit(service)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" className="text-red-500" onClick={() => handleDelete(service.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Service Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title (English)</label>
                <Input value={formData.title_en} onChange={(e) => setFormData({ ...formData, title_en: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Title (Chinese)</label>
                <Input value={formData.title_zh} onChange={(e) => setFormData({ ...formData, title_zh: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description (English)</label>
              <Textarea value={formData.description_en} onChange={(e) => setFormData({ ...formData, description_en: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description (Chinese)</label>
              <Textarea value={formData.description_zh} onChange={(e) => setFormData({ ...formData, description_zh: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Icon Name</label>
                <Input value={formData.icon} onChange={(e) => setFormData({ ...formData, icon: e.target.value })} placeholder="Home, Building2, Wrench..." />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Display Order</label>
                <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Image URL</label>
              <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={formData.active} onCheckedChange={(checked) => setFormData({ ...formData, active: checked })} />
              <span className="text-sm">Active (visible on website)</span>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-slate-900">Save Service</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminServices;
